import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  OciSelectField,
  TechneDiscoveryFieldSchema,
  TechneDiscoverySelectField,
} from './OciSelectField';
import {
  TechneCidrField,
  TechneCidrFieldSchema,
  validateTechneCidrPlan,
} from './TechneCidrField';
import {
  TechneTeamOwnerField,
  TechneTeamOwnerFieldSchema,
} from './TeamOwnerField';
import {
  TechneOkeCompartmentField,
  TechneOkeCompartmentFieldSchema,
  type OkeCompartmentValue,
} from './OkeCompartmentField';
import {
  TechneOkeTagsField,
  TechneOkeTagsFieldSchema,
  type OkeTagsValue,
} from './OkeTagsField';

const ROLE_TO_FIELD: Record<string, string> = {
  vpn: 'vpnCidr',
  infra: 'vcnInfraCidr',
  pods: 'vcnPodsCidr',
  subnetApi: 'subnetApiEndpointCidr',
  subnetWorkers: 'subnetWorkersCidr',
  subnetLb: 'subnetLoadBalancerCidr',
  subnetDb: 'subnetDatabaseCidr',
};

/** IDs únicos: scaffolder-form-field:app/oci-select e :app/ado-select. */
export const ociSelectFormField = FormFieldBlueprint.make({
  name: 'oci-select',
  params: {
    field: async () =>
      createFormField({
        name: 'OciSelect',
        component: OciSelectField,
        schema: TechneDiscoveryFieldSchema,
      }),
  },
});

export const adoSelectFormField = FormFieldBlueprint.make({
  name: 'ado-select',
  params: {
    field: async () =>
      createFormField({
        name: 'AdoSelect',
        component: TechneDiscoverySelectField,
        schema: TechneDiscoveryFieldSchema,
      }),
  },
});

export const techneCidrFormField = FormFieldBlueprint.make({
  name: 'techne-cidr',
  params: {
    field: async () =>
      createFormField({
        name: 'TechneCidr',
        component: TechneCidrField,
        schema: TechneCidrFieldSchema,
        validation: async (value, field, ctx) => {
          const current = String(value ?? '').trim();
          if (!current) return;
          const { apiHolder } = ctx;
          const formData = {
            ...((ctx.formData as Record<string, unknown>) ?? {}),
          };
          const uiSchema = (ctx as { uiSchema?: Record<string, unknown> })
            .uiSchema;
          const discoveryApi = apiHolder.get(discoveryApiRef);
          const fetchApi = apiHolder.get(fetchApiRef);
          const identityApi = apiHolder.get(identityApiRef);
          if (!discoveryApi || !fetchApi || !identityApi) return;
          const nested = (uiSchema?.['ui:options'] ?? {}) as { role?: string };
          const cidrKeys = Object.values(ROLE_TO_FIELD);
          const fieldName =
            ROLE_TO_FIELD[String(nested.role ?? '')] ??
            cidrKeys.find(key => String(formData[key] ?? '') === current) ??
            'vcnInfraCidr';
          const baseUrl = await discoveryApi.getBaseUrl('oracle-providers');
          const { token } = await identityApi.getCredentials();
          const result = await validateTechneCidrPlan({
            discoveryBaseUrl: baseUrl,
            token,
            fetch: (url, init) => fetchApi.fetch(url, init),
            formData: { ...formData, [fieldName]: current },
          });
          const mine = result.errors.filter(item => item.field === fieldName);
          if (mine[0]) {
            field.addError(mine[0].message);
          }
        },
      }),
  },
});

export const techneTeamOwnerFormField = FormFieldBlueprint.make({
  name: 'techne-team-owner',
  params: {
    field: async () =>
      createFormField({
        name: 'TechneTeamOwner',
        component: TechneTeamOwnerField,
        schema: TechneTeamOwnerFieldSchema,
      }),
  },
});

export const techneOkeCompartmentFormField = FormFieldBlueprint.make({
  name: 'techne-oke-compartment',
  params: {
    field: async () =>
      createFormField({
        name: 'TechneOkeCompartment',
        component: TechneOkeCompartmentField,
        schema: TechneOkeCompartmentFieldSchema,
        validation: async (value, field) => {
          const current = (value ?? {}) as OkeCompartmentValue;
          if (current.mode === 'create-tree') {
            const parentOcid = String(current.parentCompartmentOcid ?? '').trim();
            if (
              !parentOcid.startsWith('ocid1.compartment.') &&
              !parentOcid.startsWith('ocid1.tenancy.')
            ) {
              field.addError(
                'Escolha o compartment pai no select. (root) vale para a estrutura nova.',
              );
            }
            if (
              !String(current.treeRootName ?? '').trim() ||
              !String(current.treeFirstChildName ?? '').trim()
            ) {
              field.addError('Preencha os dois nomes da estrutura nova.');
            }
            return;
          }
          if (
            !String(current.parentCompartmentOcid ?? '').startsWith(
              'ocid1.compartment.',
            )
          ) {
            field.addError('Escolha o compartment de produto existente.');
          }
        },
      }),
  },
});

export const techneOkeTagsFormField = FormFieldBlueprint.make({
  name: 'techne-oke-tags',
  params: {
    field: async () =>
      createFormField({
        name: 'TechneOkeTags',
        component: TechneOkeTagsField,
        schema: TechneOkeTagsFieldSchema,
        validation: async (value, field) => {
          const current = (value ?? {}) as OkeTagsValue;
          if (!String(current.owner ?? '').trim()) {
            field.addError('Informe o nome do compartment PAI (owner).');
          }
          if (!String(current.businessline ?? '').trim()) {
            field.addError('Informe a business line.');
          }
          if (!String(current.costcenter ?? '').trim()) {
            field.addError('Informe o cost center.');
          }
          if (!String(current.department ?? '').trim()) {
            field.addError('Informe o department.');
          }
        },
      }),
  },
});
